/*
  HO-PERF: Hydro Oasis header scripts, moved out of inline <script> blocks so they are downloaded once and
  cached instead of being re-sent with every HTML page (the search script also ran twice per page, once per
  search copy). Loaded once, async, at the end of sections/navigation-main.liquid, so the header markup is
  already parsed when it runs, as it was for the old inline scripts. Each init stays idempotent (loaded/bound
  guards) and still re-runs on DOMContentLoaded and shopify:section:load (theme editor).
  Markup: snippets/hydro-oasis-predictive-search.liquid, hydro-oasis-explore-menu.liquid,
  hydro-oasis-navigation-dropdown-polish.liquid, component-navigation-block.liquid. CSS: assets/ho-header.css,
  assets/ho-header-desktop.css. All queries are scoped to #nav-wrapper (layout/theme.liquid).
*/
(() => {
  // The theme editor can re-insert this tag with a re-rendered header section: the shopify:section:load
  // listeners below already re-initialise the new markup, so a second copy must not bind everything again.
  if (window.__hoHeaderJs) return;
  window.__hoHeaderJs = true;

  /* ---------- (1) Predictive search: snippets/hydro-oasis-predictive-search.liquid ---------- */
  (() => {
    const initHydroOasisSearch = () => {
      const roots = document.querySelectorAll('#nav-wrapper [data-ho-search]:not([data-ho-search-loaded="true"])');

      roots.forEach((root) => {
        root.dataset.hoSearchLoaded = 'true';

        const form = root.querySelector('.ho-search__form');
        const input = root.querySelector('[data-ho-search-input]');
        const mobileInput = root.querySelector('[data-ho-search-mobile-input]');
        const mobileForm = root.querySelector('[data-ho-mobile-form]');
        const dropdown = root.querySelector('[data-ho-search-dropdown]');
        const defaultState = root.querySelector('[data-ho-search-default]');
        const resultsState = root.querySelector('[data-ho-search-results]');
        const emptyState = root.querySelector('[data-ho-search-empty]');
        const resultsGrid = root.querySelector('[data-ho-results-grid]');
        const viewAll = root.querySelector('[data-ho-view-all]');
        const recentList = root.querySelector('[data-ho-recent-list]');
        const clearRecent = root.querySelector('[data-ho-clear-recent]');

        if (!form || !input || !dropdown) return;

        // HO-PERF: the inline script printed Liquid routes.search_url here; the form's action is that same
        // (locale-aware) URL, so this file needs no Liquid.
        const searchUrl = form.getAttribute('action') || '/search';

        const storageKey = 'hydro-oasis-recent-searches';
        let controller = null;
        let debounceTimer = null;
        let queryRevision = 0;

        const setTopPosition = () => {
          if (dropdown.hidden) return;
          const rect = form.getBoundingClientRect();
          document.documentElement.style.setProperty('--ho-search-top', `${Math.max(rect.bottom + 8, 72)}px`);
        };

        const openDropdown = (focusMobileInput = false) => {
          // Opening work is independent of typing into an already open dropdown.
          if (dropdown.hidden) {
            dropdown.hidden = false;
            setTopPosition();
            root.classList.add('is-open');
            renderRecentSearches();
          }

          if (focusMobileInput && mobileInput && window.matchMedia('(max-width: 1550px)').matches) {
            window.requestAnimationFrame(() => mobileInput.focus());
          }
        };

        const closeDropdown = () => {
          dropdown.hidden = true;
          root.classList.remove('is-open');
        };

        const getRecentSearches = () => {
          try {
            return JSON.parse(localStorage.getItem(storageKey)) || [];
          } catch {
            return [];
          }
        };

        const saveRecentSearch = (term) => {
          const cleanTerm = term.trim();
          if (!cleanTerm) return;

          const existing = getRecentSearches().filter((item) => item.toLowerCase() !== cleanTerm.toLowerCase());
          const updated = [cleanTerm, ...existing].slice(0, 5);
          localStorage.setItem(storageKey, JSON.stringify(updated));
          renderRecentSearches();
        };

        const renderRecentSearches = () => {
          if (!recentList) return;

          const terms = getRecentSearches();
          recentList.innerHTML = '';

          terms.forEach((term) => {
            const li = document.createElement('li');
            const link = document.createElement('a');
            link.href = `${searchUrl}?q=${encodeURIComponent(term)}`;
            link.textContent = term;
            li.appendChild(link);
            recentList.appendChild(li);
          });
        };

        const getImageUrl = (item) => {
          if (item.image) return item.image;
          if (item.featured_image && item.featured_image.url) return item.featured_image.url;
          if (item.featured_image) return item.featured_image;
          return '';
        };

        const createCard = (item) => {
          const card = document.createElement('a');
          card.className = 'ho-search__card';
          card.href = item.url || '#';

          const imageWrap = document.createElement('span');
          imageWrap.className = 'ho-search__image-wrap';

          const imageUrl = getImageUrl(item);
          if (imageUrl) {
            const img = document.createElement('img');
            img.className = 'ho-search__image';
            img.src = imageUrl;
            img.alt = item.title || '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.width = 260;
            img.height = 260;
            imageWrap.appendChild(img);
          }

          // HO-CUSTOM: discount % badge on product results (predictive API gives
          // price + compare_at_price; inventory policy isn't available here, so
          // back-order/pre-order can't be shown in the dropdown).
          const cmp = parseFloat(item.compare_at_price_min);
          const pr = parseFloat(item.price_min);
          if (!isNaN(cmp) && !isNaN(pr) && cmp > pr) {
            const pct = Math.round(((cmp - pr) / cmp) * 100);
            if (pct >= 1) {
              const disc = document.createElement('span');
              disc.className = 'ho-search__discount';
              disc.textContent = '-' + pct + '%';
              imageWrap.appendChild(disc);
            }
          }

          const title = document.createElement('span');
          title.className = 'ho-search__card-title';
          title.textContent = item.title || '';

          card.appendChild(imageWrap);
          card.appendChild(title);

          return card;
        };

        const showDefault = () => {
          if (defaultState) defaultState.hidden = false;
          if (resultsState) resultsState.hidden = true;
          if (emptyState) emptyState.hidden = true;
        };

        const showResults = (items, term) => {
          if (!resultsGrid) return;

          resultsGrid.innerHTML = '';

          items.forEach((item) => {
            resultsGrid.appendChild(createCard(item));
          });

          if (viewAll) viewAll.href = `${searchUrl}?q=${encodeURIComponent(term)}`;

          if (defaultState) defaultState.hidden = true;
          if (resultsState) resultsState.hidden = false;
          if (emptyState) emptyState.hidden = true;
        };

        const showEmpty = () => {
          if (defaultState) defaultState.hidden = true;
          if (resultsState) resultsState.hidden = true;
          if (emptyState) emptyState.hidden = false;
        };

        const searchSuggest = async (term, revision) => {
          if (revision !== queryRevision) return;
          const cleanTerm = term.trim();

          if (!cleanTerm) {
            if (controller) controller.abort();
            showDefault();
            return;
          }

          const requestController = new AbortController();
          controller = requestController;

          try {
            const url = `/search/suggest.json?q=${encodeURIComponent(cleanTerm)}&resources[type]=product,collection,article,page&resources[limit]=8`;
            const response = await fetch(url, { signal: requestController.signal });
            const data = await response.json();
            // An abort alone cannot guard a response whose JSON is already settling.
            if (revision !== queryRevision || requestController.signal.aborted) return;
            const results = data.resources && data.resources.results ? data.resources.results : {};
            const items = [
              ...(results.products || []),
              ...(results.collections || []),
              ...(results.articles || []),
              ...(results.pages || [])
            ].slice(0, 12);

            if (items.length) {
              showResults(items, cleanTerm);
            } else {
              showEmpty();
            }
          } catch (error) {
            if (revision === queryRevision && !requestController.signal.aborted && error.name !== 'AbortError') showEmpty();
          }
        };

        const queueSearch = (value) => {
          openDropdown();
          clearTimeout(debounceTimer);
          const revision = ++queryRevision;
          // Supersede an in-flight query now, rather than after the next debounce.
          if (controller) controller.abort();
          debounceTimer = setTimeout(() => searchSuggest(value, revision), 180);
        };

        input.addEventListener('focus', () => openDropdown());
        input.addEventListener('click', () => openDropdown(true));
        input.addEventListener('input', () => {
          if (mobileInput && mobileInput.value !== input.value) mobileInput.value = input.value;
          queueSearch(input.value);
        });

        if (mobileInput) {
          mobileInput.addEventListener('input', () => {
            if (input && input.value !== mobileInput.value) input.value = mobileInput.value;
            queueSearch(mobileInput.value);
          });
        }

        form.addEventListener('submit', (event) => {
          if (window.matchMedia('(max-width: 1550px)').matches && !input.value.trim()) {
            event.preventDefault();
            openDropdown(true);
            return;
          }

          saveRecentSearch(input.value);
        });

        if (mobileForm) {
          mobileForm.addEventListener('submit', () => {
            if (input && mobileInput) input.value = mobileInput.value;
            saveRecentSearch(mobileInput ? mobileInput.value : '');
          });
        }

        if (clearRecent) {
          clearRecent.addEventListener('click', () => {
            localStorage.removeItem(storageKey);
            renderRecentSearches();
          });
        }

        document.addEventListener('click', (event) => {
          if (!root.contains(event.target) && !dropdown.contains(event.target)) {
            closeDropdown();
          }
        });

        window.addEventListener('resize', setTopPosition);
        window.addEventListener('scroll', setTopPosition, { passive: true });

        renderRecentSearches();
      });
    };

    document.addEventListener('DOMContentLoaded', initHydroOasisSearch);
    document.addEventListener('shopify:section:load', initHydroOasisSearch);
    initHydroOasisSearch();
  })();


  /* ---------- (2) Explore dropdown: snippets/hydro-oasis-explore-menu.liquid ---------- */
  (() => {
    const initHydroOasisExplore = () => {
      const roots = document.querySelectorAll('#nav-wrapper [data-ho-explore]:not([data-ho-explore-loaded="true"])');

      roots.forEach((root) => {
        root.dataset.hoExploreLoaded = 'true';

        const trigger = root.querySelector('[data-ho-explore-trigger]');
        const dropdown = root.querySelector('[data-ho-explore-dropdown]');
        const tabs = root.querySelectorAll('[data-ho-explore-tab]');
        const panels = root.querySelectorAll('[data-ho-explore-panel]');

        if (!trigger || !dropdown) return;

        const setTopPosition = () => {
          // The dropdown is position:fixed and only needs its top recomputed while open,
          // so skip the layout read/write on resize/scroll when it's closed. openDropdown()
          // calls this directly before showing, so the value is always fresh when visible.
          if (dropdown.hidden) return;
          const rect = trigger.getBoundingClientRect();
          document.documentElement.style.setProperty('--ho-explore-top', `${Math.max(rect.bottom + 8, 72)}px`);
        };

        const openDropdown = () => {
          // Unhide first so setTopPosition() (guarded against the hidden state) runs.
          dropdown.hidden = false;
          setTopPosition();
          root.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        };

        const closeDropdown = () => {
          dropdown.hidden = true;
          root.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        };

        const toggleDropdown = () => {
          if (dropdown.hidden) {
            openDropdown();
          } else {
            closeDropdown();
          }
        };

        const activatePanel = (handle) => {
          tabs.forEach((tab) => {
            const isActive = tab.dataset.hoExploreTab === handle;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.tabIndex = isActive ? 0 : -1;
          });

          panels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.hoExplorePanel === handle);
          });
        };

trigger.addEventListener('click', (event) => {
  event.preventDefault();
  toggleDropdown();
});

        const tabList = Array.from(tabs);
        tabs.forEach((tab) => {
          tab.addEventListener('mouseenter', () => activatePanel(tab.dataset.hoExploreTab));
          tab.addEventListener('click', () => activatePanel(tab.dataset.hoExploreTab));
          tab.addEventListener('keydown', (event) => {
            const idx = tabList.indexOf(tab);
            let nextIdx = null;
            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
              nextIdx = (idx + 1) % tabList.length;
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
              nextIdx = (idx - 1 + tabList.length) % tabList.length;
            } else if (event.key === 'Home') {
              nextIdx = 0;
            } else if (event.key === 'End') {
              nextIdx = tabList.length - 1;
            }
            if (nextIdx === null) return;
            event.preventDefault();
            const nextTab = tabList[nextIdx];
            activatePanel(nextTab.dataset.hoExploreTab);
            nextTab.focus();
          });
        });

        document.addEventListener('click', (event) => {
          if (!root.contains(event.target) && !dropdown.contains(event.target)) {
            closeDropdown();
          }
        });

        window.addEventListener('resize', setTopPosition);
        window.addEventListener('scroll', setTopPosition, { passive: true });
      });
    };

    document.addEventListener('DOMContentLoaded', initHydroOasisExplore);
    document.addEventListener('shopify:section:load', initHydroOasisExplore);
    initHydroOasisExplore();
  })();


  /* ---------- (3) Shop mega-menu polish: snippets/hydro-oasis-navigation-dropdown-polish.liquid ---------- */
  /* Track the Shop trigger's bottom edge into --ho-mega-top so the (now
     position:fixed) mega-menu opens just under the nav row, mirroring the
     Explore dropdown. Self-contained: no dependency on the Explore script.
     HO-PERF: desktop only. The Shop mega menu sits in the .hide-mobile header column, so below 64em
     nothing here runs: no forced layout while the page parses, at DOMContentLoaded/load or on every
     resize (Android fires resize as the URL bar shows/hides). Rotating or resizing into desktop width
     sets it up then. The top is measured only just before the panel opens (trigger pointerenter/focusin
     and the patched toggletip update() below), never at parse time. */
  (() => {
    const mq = window.matchMedia('(min-width: 64em)');
    const getTrigger = () => {
      const root = document.getElementById('nav-wrapper');
      if (!root) return null;
      const dropdown = root.querySelector('.nav.nav--one-level.tooltip--large');
      const item = (dropdown && dropdown.closest('.main-nav-item')) || root.querySelector('.main-nav-item.one_level_megamenu');
      return item && (item.querySelector('[data-tooltip-trigger]') || item.querySelector('a'));
    };
    const updateMegaTop = () => {
      if (!mq.matches) return;
      const trigger = getTrigger();
      if (!trigger) return;
      // Recompute from the TRIGGER's live position every time. (The listener is
      // bound to the trigger only, so this no longer nudges on dropdown-link
      // hover; and the engine-update patch below also re-reads it live, so a
      // stale value can't leave the panel parked below the features bar.)
      // 8px gap below the trigger: matches the Explore + Search dropdowns
      // (both use trigger.bottom + 8) so all three open with the same spacing.
      const top = Math.max(Math.round(trigger.getBoundingClientRect().bottom + 8), 72);
      document.documentElement.style.setProperty('--ho-mega-top', top + 'px');
    };
    // Bind a fresh read to the TRIGGER only (fires just before it opens), so a
    // web-font/image reflow can't leave the top edge stale: without listening
    // on the whole item (which includes the dropdown links). Binding itself does not measure.
    const bindTrigger = () => {
      const trigger = getTrigger();
      if (!trigger || trigger.__hoMegaTop) return;
      trigger.__hoMegaTop = true;
      trigger.addEventListener('pointerenter', updateMegaTop);
      trigger.addEventListener('focusin', updateMegaTop);
    };
    let resizeQueued = false;
    window.addEventListener('resize', () => {
      if (resizeQueued || !mq.matches) return;
      resizeQueued = true;
      window.requestAnimationFrame(() => {
        resizeQueued = false;
        updateMegaTop();
      });
    });

    // Defensive lock: the theme's tooltip engine can write inline positioning
    // (top/transform/etc.) onto the panel as the mouse moves over the nav, which
    // shifted our centred fixed panel down the screen. Strip those inline props
    // whenever they appear so our !important CSS (position:fixed; top:var;
    // translateX(-50%)) always governs. visibility/opacity are left untouched so
    // the open/close still works.
    const lockPanel = () => {
      const dropdown = document.querySelector('#nav-wrapper .nav.nav--one-level.tooltip--large')
        || document.querySelector('.nav.nav--one-level.tooltip--large');
      if (!dropdown || dropdown.__hoPosLock) return;
      dropdown.__hoPosLock = true;
      const posProps = ['top', 'bottom', 'left', 'right', 'inset', 'margin', 'margin-top', 'transform'];
      let stripping = false;
      const strip = () => {
        if (stripping) return;
        const st = dropdown.style;
        if (posProps.some((p) => st.getPropertyValue(p))) {
          stripping = true;
          posProps.forEach((p) => st.removeProperty(p));
          stripping = false;
        }
      };
      strip();
      new MutationObserver(strip).observe(dropdown, { attributes: true, attributeFilter: ['style'] });
    };

    // Strongest lock: the theme's <toggletip-engine> repositions the panel via
    // its own update() (called on hover and in a rAF loop), writing inline
    // top/left/transform that raced: and beat: the MutationObserver above, so
    // the panel dropped into the hero on real hover. Patch update() to strip
    // that inline positioning from OUR mega panel the instant the engine writes
    // it (and clear any transform on wrapper ancestors, which would otherwise
    // re-anchor our position:fixed panel to a transformed box). Our !important
    // CSS (position:fixed; top:var(--ho-mega-top); translateX(-50%)) then always
    // governs. Update-safe prototype patch: never edits base.js.
    const POS_PROPS = ['top', 'bottom', 'left', 'right', 'inset', 'inset-block', 'inset-inline', 'margin', 'margin-top', 'margin-bottom', 'transform'];
    const patchEngine = () => {
      const ctor = window.customElements && customElements.get('toggletip-engine');
      if (!ctor) {
        if (window.customElements) customElements.whenDefined('toggletip-engine').then(patchEngine);
        return;
      }
      if (ctor.prototype.__hoNavPosPatch) return;
      const orig = ctor.prototype.update;
      if (typeof orig !== 'function') return;
      ctor.prototype.__hoNavPosPatch = true;
      ctor.prototype.update = function () {
        const ret = orig.apply(this, arguments);
        try {
          const panel = this.querySelector && this.querySelector('.nav.nav--one-level.tooltip--large');
          if (panel) {
            POS_PROPS.forEach((p) => panel.style.removeProperty(p));
            // Glue the anchor to the live trigger position so layout shifts
            // (marquee/announcement bar height, image/font reflow, zoom) can't
            // leave a stale --ho-mega-top parking the panel below the features bar.
            const item = panel.closest('.main-nav-item');
            const trig = item && item.querySelector('[data-tooltip-trigger]');
            if (trig) {
              const t = Math.max(Math.round(trig.getBoundingClientRect().bottom + 8), 72);
              document.documentElement.style.setProperty('--ho-mega-top', t + 'px');
            }
            // kill transform/filter/perspective on wrappers between panel and engine
            let el = panel.parentElement;
            while (el && el !== this) {
              if (el.style) { el.style.removeProperty('transform'); el.style.removeProperty('perspective'); el.style.removeProperty('filter'); }
              el = el.parentElement;
            }
            if (this.style) this.style.removeProperty('transform');
          }
        } catch (e) {
          // Storefront: stay silent so a single engine-internals change can't spam
          // the console on every update(). In the theme editor, surface it so the
          // breakage is diagnosable.
          if (window.Shopify && Shopify.designMode) console.warn('[ho-nav] engine patch failed', e);
        }
        return ret;
      };
    };

    let ready = false;
    const setupDesktop = () => {
      if (ready || !mq.matches) return;
      ready = true;
      bindTrigger();
      lockPanel();
      patchEngine();
    };
    const onMqChange = () => {
      if (mq.matches) setupDesktop();
    };
    if (mq.addEventListener) mq.addEventListener('change', onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange);
    // Theme editor: a re-rendered header has a new trigger and panel to bind and lock.
    document.addEventListener('shopify:section:load', () => {
      ready = false;
      setupDesktop();
    });
    setupDesktop();
  })();

  /* ---------- (4) Hamburger menu features bar ---------- */
  /* HO-PERF: the hamburger menu's features bar (snippets/component-navigation-block.liquid,
     data-ho-autoplay-when-open) is an initialised, looping, autoplaying slider even while the menu is
     closed (laid out off-screen), so its 5 s autoplay kept a rAF loop running and moved DOM nodes on
     every page. Autoplay now runs only while the menu is open: toggletip-engine sets open="true" on the
     menu trigger and removes it on close. Swiping and the arrows are unchanged. */
  (() => {
    const SEL = 'swiper-container[data-ho-autoplay-when-open]';
    const bindAwo = (el) => {
      const eng = el.closest('toggletip-engine');
      const trig = eng && eng.querySelector('[data-tooltip-trigger]');
      if (!trig) return;
      const sync = () => {
        // el.swiper is re-created when utilities-swiper-element.js re-initialises (resize across the
        // breakpoint, theme editor), so always read it live.
        const sw = el.swiper;
        if (!sw || sw.destroyed || !sw.autoplay || !sw.params || !sw.params.autoplay || !sw.params.autoplay.enabled) return;
        if (trig.getAttribute('open') === 'true') {
          if (!sw.autoplay.running) sw.autoplay.start();
        } else if (sw.autoplay.running) {
          sw.autoplay.stop();
        }
      };
      if (!el.__hoAwo) {
        el.__hoAwo = true;
        new MutationObserver(sync).observe(trig, { attributes: true, attributeFilter: ['open'] });
      }
      sync();
    };
    const bindAll = () => document.querySelectorAll(SEL).forEach(bindAwo);
    bindAll();
    // The slider is initialised by deferred theme JS, normally before DOMContentLoaded, but this file is
    // async and may run either side of it.
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindAll);
    // Late or theme-editor initialisations. utilities-swiper-element.js does its own afterinit work in a
    // setTimeout 0 (and Swiper starts autoplay on init), so queue behind it.
    document.addEventListener('swiperafterinit', (event) => {
      const el = event.target;
      if (el && el.matches && el.matches(SEL)) setTimeout(() => bindAwo(el), 0);
    });
  })();
})();
